import { FC, useCallback, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/nextjs';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import ExternalLink from 'src/components/ExternalLink';
import GemImage from 'src/components/GemImage';
import LoadingIndicator from 'src/components/LoadingIndicator';
import PaymentsHeader from 'src/components/Payments/Header';
import PaymentConfirmButton from 'src/components/Payments/PaymentConfirmButton';
import PurchasableProductIcon from 'src/components/Payments/ProductIcon';
import { ProductLabelStyled, ProductLabelWrapper as ProductLabelWrapperStyled, Container, Divider, PaymentConfirmButtonWrapper, RootStyle } from 'src/components/Payments/style';
import { useCompletePaymentMutation } from 'src/hooks/usePaymentQuery';
import useQueryParams from 'src/hooks/useQueryParams';
import { azarTokenAtom, userCountryCodeAtom, userDataAtom } from 'src/stores/auth/atoms';
import { paymentPopupInfoAtom } from 'src/stores/payments/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { PurchaseCompleteMessage, WindowMessageType } from 'src/types/message';
import { isPurchasableProductModule, PaymentsCompleteRequest, PaymentsCompleteResponse, PgPaymentMethod } from 'src/types/Payments';
import { pushAppsflyerEvent } from 'src/utils/appsflyer/pba';
import { numComma } from 'src/utils/common';
import { pushEvent } from 'src/utils/event-gateway';
import getGemProductName, { getProductCalcGem } from 'src/utils/payments';
export const Description = styled.div`
  font-size: 15px;
  color: ${({
  theme
}) => theme.color.content8};
  margin: 0 24px 32px 24px;
`;
export const Box = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 24px 0 24px;

  & + & {
    margin-top: 8px;
  }
`;
export const PriceLabel = styled.div`
  color: ${({
  theme
}) => theme.color.content12};
  font-size: 16px;
`;
export const PriceText = styled.div`
  color: ${({
  theme
}) => theme.color.content12};
  font-size: 16px;
  font-weight: bold;
`;
export const OrderInfoWrapper = styled.div`
  overflow: scroll;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
  min-height: 0;
  flex: 1 1 0;
`;
export const OrderInfoText = styled.div`
  color: ${({
  theme
}) => theme.color.content8};
  font-size: 15px;
`;
export const OrderInfoTextLabel = styled(OrderInfoText)`
  white-space: nowrap;
  flex-shrink: 0;
`;
export const Footer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;
export const Gradient = styled.div`
  background: linear-gradient(
    180deg,
    rgba(34, 34, 34, 0) 0%,
    ${({
  theme
}) => theme.color.surface2} 100%
  );
  height: 56px;
  width: 100%;
  position: absolute;
  transform: translateY(-100%);
`;
export const InvoiceButton = styled(ExternalLink)`
  font-size: 15px;
  font-weight: bold;
  color: ${({
  theme
}) => theme.color.content8};
  margin-top: 18px;
  cursor: pointer;
  text-decoration: none;
`;
export const ImgGemSquare = styled(GemImage)`
  width: 48px;
  height: 48px;
  ${({
  theme
}) => theme.isRtl ? 'margin-left' : 'margin-right'}: 18px;
`;
export const ProductLabel = styled(ProductLabelStyled)`
  font-size: 20px;
`;
export const ProductLabelWrapper = styled(ProductLabelWrapperStyled)`
  padding: ${({
  theme
}) => theme.isRtl ? '18px 0px 30px 0px' : '18px 0px 30px 0px'};
`;
const PaymentSuccessView: FC = () => {
  const {
    t
  } = useTranslation();
  const userData = useAtomValue(userDataAtom);
  const userId = userData?.userId || '';
  const {
    paymentKey,
    orderId
  } = useQueryParams<PaymentsCompleteRequest>() || {};
  const paymentPopupInfo = useAtomValue(paymentPopupInfoAtom);
  const handlePaymentCompleteSuccess = useCallback((completedOrderInfo: PaymentsCompleteResponse) => {
    if (!paymentPopupInfo) {
      Sentry.captureMessage('Payment popup info missing after completing purchase', {
        extra: {
          orderId
        }
      });
    }
    const {
      product,
      amount,
      currency,
      paymentMethod
    } = completedOrderInfo;
    const origins = paymentPopupInfo?.origins ?? [];
    const purchasedProduct = paymentPopupInfo?.product;
    pushEvent({
      eventType: EVENT_TYPE.PURCHASE,
      eventName: EVENT_NAME.PURCHASE_COMPLETE,
      eventParams: {
        action_category: 'action',
        page: 'anywhere',
        target: 'purchase_complete',
        payment_method: paymentMethod,
        // 가격 정보
        value: amount,
        currency,
        transaction_id: orderId,
        quantity: getProductCalcGem(product).num,
        origin: origins,
        product_id: purchasedProduct?.productId,
        tab: paymentPopupInfo?.originTab
      },
      /*
       * dateLayer에 user_id를 전달하기 위해 options에 추가한다.
       * @see https://hpcnt.slack.com/archives/C02SQS5AJDR/p1776404459768479?thread_ts=1775438655.132339&cid=C02SQS5AJDR
       */
      options: {
        user_id: userId
      }
    });
    pushAppsflyerEvent('purchase', {
      eventRevenue: amount,
      eventRevenueCurrency: currency
    });
    if (purchasedProduct && window.opener) {
      // 메인 창에 결제 완료를 공유
      const message: PurchaseCompleteMessage = {
        type: WindowMessageType.PurchaseComplete,
        productModuleType: !product.isGemProduct && isPurchasableProductModule(purchasedProduct) ? purchasedProduct.moduleType : undefined
      };
      window.opener.postMessage(message);
    }
  }, [orderId, userId, paymentPopupInfo]);
  const {
    mutate,
    data,
    isSuccess
  } = useCompletePaymentMutation({
    onSuccess: response => {
      handlePaymentCompleteSuccess(response.data);
    }
  });
  const orderInfo = data?.data;
  useEffect(() => {
    if (!orderId) return;
    mutate({
      paymentKey,
      orderId
    });
  }, [orderId, paymentKey, mutate]);
  const azarToken = useAtomValue(azarTokenAtom);
  const countryCode = useAtomValue(userCountryCodeAtom);
  const hasInvoice = useMemo(() => {
    return orderInfo?.paymentMethod === PgPaymentMethod.ADYEN && ['SA', 'AE'].includes(countryCode || '');
  }, [countryCode, orderInfo?.paymentMethod]);
  const productName = useMemo(() => {
    if (!orderInfo?.product) return;
    return orderInfo.product.isGemProduct ? getGemProductName(orderInfo.product) // 젬 상품은 젬수량 합쳐서 임의로 이름 생성
    : orderInfo.product.productName;
  }, [orderInfo?.product]);
  return <RootStyle data-testid='Payments-Success' data-sentry-element="RootStyle" data-sentry-component="PaymentSuccessView" data-sentry-source-file="PaymentSuccess.tsx">
      <PaymentsHeader title='pg_purchase_complete' isPaymentComplete={isSuccess} data-sentry-element="PaymentsHeader" data-sentry-source-file="PaymentSuccess.tsx" />
      <Container data-sentry-element="Container" data-sentry-source-file="PaymentSuccess.tsx">
        {orderInfo ? <>
            <ProductLabelWrapper>
              <PurchasableProductIcon />
              <ProductLabel>{productName}</ProductLabel>
            </ProductLabelWrapper>
            <Description>{t('pg_purchase_itempending')}</Description>
            <Box>
              <PriceLabel>{t('pg_purchase_price')}</PriceLabel>
              <PriceText>
                {`${getSymbolFromCurrency(orderInfo.currency)}${numComma(orderInfo.amount)}`}
              </PriceText>
            </Box>
            <Divider />
            <OrderInfoWrapper>
              <Box>
                <OrderInfoTextLabel>{t('pg_purchase_orderid')}</OrderInfoTextLabel>
                <OrderInfoText data-testid='Payments-Success-orderid'>{orderId}</OrderInfoText>
              </Box>
            </OrderInfoWrapper>
            <Footer>
              <Gradient />
              <PaymentConfirmButtonWrapper>
                <PaymentConfirmButton>{t('COMMON__OK')}</PaymentConfirmButton>
                {hasInvoice && azarToken && <InvoiceButton data-testid='Payments-Success-invoice' href={`${process.env.NEXT_PUBLIC_INVOICE_WEBVIEW_URL}/?serviceCode=AZAR&token=${azarToken.accessToken}`}>
                    {t('pg_invoice')}
                  </InvoiceButton>}
              </PaymentConfirmButtonWrapper>
            </Footer>
          </> : <LoadingIndicator size={30} />}
      </Container>
    </RootStyle>;
};
export default PaymentSuccessView;